import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../hooks/useApi';
import { useAppState } from '../../context/AppStateContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Modal from '../../components/common/Modal';
import AcademicLeadershipManager from '../../components/admin/AcademicLeadershipManager';
import SearchableDropdown from '../../components/common/SearchableDropdown';

const StreamSection = ({ gradeId, streams, teachers, onAction }) => {
    const [isAdding, setIsAdding] = useState(false);
    const [newStream, setNewStream] = useState({ name: '', class_teacher: '', assistant_class_teacher: '' });

    const handleAdd = async () => {
        if (!newStream.name) return;
        await onAction('add', { ...newStream, grade_level: gradeId });
        setNewStream({ name: '', class_teacher: '', assistant_class_teacher: '' });
        setIsAdding(false);
    };

    return (
        <div className="p-6">
            <div className="flex items-center justify-between mb-4">
                <div>
                    <h4 className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">Enrollment Streams</h4>
                </div>
                <button
                    onClick={() => setIsAdding(true)}
                    className="p-2 bg-indigo-600 text-white rounded-lg text-[10px] hover:bg-indigo-700 transition-all flex items-center gap-2 shadow-lg shadow-indigo-900/20"
                >
                    <i className="fas fa-plus"></i>
                </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {streams.map(stream => (
                    <div key={stream.id} className="bg-slate-50 rounded-2xl p-4 border border-slate-100 group relative">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-black text-[#18216D] uppercase">{stream.name}</span>
                            <button
                                onClick={() => onAction('delete', stream.id)}
                                className="text-rose-400 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                            >
                                <i className="fas fa-trash-alt text-[10px]"></i>
                            </button>
                        </div>
                        <div className="space-y-3 mt-2">
                            <div>
                                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Class Teacher</label>
                                <SearchableDropdown
                                    options={teachers}
                                    selectedValues={stream.class_teacher ? [stream.class_teacher] : []}
                                    onChange={(id) => onAction('update', { id: stream.id, class_teacher: id })}
                                    placeholder="Unassigned"
                                    isMultiple={false}
                                    displayKey="name"
                                    compact={true}
                                />
                            </div>
                            <div>
                                <label className="text-[8px] font-black text-slate-400 uppercase tracking-widest block mb-1">Asst. Teacher</label>
                                <SearchableDropdown
                                    options={teachers}
                                    selectedValues={stream.assistant_class_teacher ? [stream.assistant_class_teacher] : []}
                                    onChange={(id) => onAction('update', { id: stream.id, assistant_class_teacher: id })}
                                    placeholder="Unassigned"
                                    isMultiple={false}
                                    displayKey="name"
                                    compact={true}
                                />
                            </div>
                        </div>
                    </div>
                ))}

                {isAdding && (
                    <div className="bg-white border-2 border-dashed border-indigo-200 rounded-3xl p-6 animate-in zoom-in-95 duration-200 col-span-1 md:col-span-2 shadow-xl shadow-indigo-900/5">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex-1">
                                <label className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-1.5 block">Identity & Designation</label>
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="e.g. 7 EAST"
                                    value={newStream.name}
                                    onChange={(e) => setNewStream(prev => ({ ...prev, name: e.target.value }))}
                                    className="bg-slate-50 border-none rounded-2xl w-full px-5 py-4 text-sm font-black text-[#18216D] uppercase focus:ring-4 focus:ring-indigo-50 placeholder:text-slate-300 transition-all mb-4"
                                />

                                <label className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-1.5 block mt-4">Assign Class Teacher</label>
                                <SearchableDropdown
                                    options={teachers}
                                    selectedValues={newStream.class_teacher ? [newStream.class_teacher] : []}
                                    onChange={(id) => setNewStream(prev => ({ ...prev, class_teacher: id }))}
                                    placeholder="Select Teacher (Optional)"
                                    isMultiple={false}
                                    displayKey="name"
                                    compact={true}
                                />

                                <label className="text-[9px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-1.5 block mt-4">Assign Asst. Teacher</label>
                                <SearchableDropdown
                                    options={teachers}
                                    selectedValues={newStream.assistant_class_teacher ? [newStream.assistant_class_teacher] : []}
                                    onChange={(id) => setNewStream(prev => ({ ...prev, assistant_class_teacher: id }))}
                                    placeholder="Select Asst. Teacher (Optional)"
                                    isMultiple={false}
                                    displayKey="name"
                                    compact={true}
                                />
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button
                                onClick={handleAdd}
                                className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-900/20 hover:bg-indigo-700 transition-all"
                            >
                                Save Stream
                            </button>
                            <button
                                onClick={() => setIsAdding(false)}
                                className="px-6 py-4 bg-slate-50 text-slate-400 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-100 transition-all"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const GradeItem = ({ grade, teachers, onSave, onStreamAction }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [localData, setLocalData] = useState({
        teacher_id: grade.class_teacher_id || '',
        formative_weight: grade.formative_weight || 40,
        summative_weight: grade.summative_weight || 60
    });
    const [submitting, setSubmitting] = useState(false);

    const totalWeight = parseFloat(localData.formative_weight) + parseFloat(localData.summative_weight);
    const isValid = totalWeight === 100;
    const hasChanges = localData.teacher_id !== grade.class_teacher_id ||
        localData.formative_weight !== grade.formative_weight ||
        localData.summative_weight !== grade.summative_weight;

    const handleSave = async (e) => {
        e.stopPropagation();
        if (!isValid) return;
        setSubmitting(true);
        try {
            await onSave(grade.id, localData);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={`bg-white rounded-[2rem] shadow-sm border transition-all duration-500 ${isOpen ? 'border-indigo-100 shadow-xl shadow-indigo-900/5' : 'border-slate-100'}`}>
            {/* Header / Clickable Area */}
            <div className={`p-5 cursor-pointer flex flex-col md:flex-row md:items-center justify-between gap-6 group transition-colors ${isOpen ? 'bg-slate-50/30' : 'hover:bg-slate-50/30'}`} onClick={() => setIsOpen(!isOpen)}>
                <div className="flex items-center gap-6">
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center transition-all duration-500 shadow-lg ${isOpen ? 'bg-[#FFC425] -rotate-6' : 'bg-[#18216D]'}`}>
                        <i className={`fas fa-graduation-cap text-lg transition-colors ${isOpen ? 'text-[#18216D]' : 'text-[#FFC425]'}`}></i>
                    </div>
                    <div>
                        <h3 className="text-xl font-black text-[#18216D] uppercase tracking-tighter">{grade.name}</h3>
                        <div className="flex items-center gap-3 mt-0.5">
                            <span className="text-indigo-900/40 text-[9px] font-black uppercase tracking-widest">
                                {grade.streams?.length || 0} Streams
                            </span>
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-6">
                    {/* Weights Surfaced on Main Card - Increased Size */}
                    <div className="flex items-center gap-6 bg-white p-3 rounded-2xl border border-slate-100 shadow-sm" onClick={(e) => e.stopPropagation()}>
                        <div className="flex flex-col items-center px-4 border-r border-slate-100">
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Formative</span>
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    value={localData.formative_weight}
                                    onChange={(e) => {
                                        const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                        setLocalData(prev => ({ ...prev, formative_weight: val, summative_weight: 100 - val }));
                                    }}
                                    className="w-12 p-0 bg-transparent border-none text-base font-black text-[#18216D] text-center focus:ring-0"
                                />
                                <span className="text-xs text-slate-300 font-bold">%</span>
                            </div>
                        </div>
                        <div className="flex flex-col items-center px-4">
                            <span className="text-[10px] font-black text-slate-300 uppercase tracking-widest mb-1">Summative</span>
                            <div className="flex items-center gap-1">
                                <input
                                    type="number"
                                    value={localData.summative_weight}
                                    onChange={(e) => {
                                        const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                        setLocalData(prev => ({ ...prev, summative_weight: val, formative_weight: 100 - val }));
                                    }}
                                    className="w-12 p-0 bg-transparent border-none text-base font-black text-[#18216D] text-center focus:ring-0"
                                />
                                <span className="text-xs text-slate-300 font-bold">%</span>
                            </div>
                        </div>
                        {hasChanges && (
                            <button
                                onClick={handleSave}
                                disabled={!isValid || submitting}
                                className="ml-2 w-10 h-10 bg-indigo-600 text-white rounded-xl flex items-center justify-center hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-900/20"
                            >
                                <i className={`fas ${submitting ? 'fa-spinner fa-spin' : 'fa-check'} text-xs`}></i>
                            </button>
                        )}
                    </div>

                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all ${isOpen ? 'bg-indigo-50 text-indigo-600 rotate-180' : 'bg-slate-50 text-slate-400'}`}>
                        <i className="fas fa-chevron-down text-xs"></i>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isOpen && (
                <div className="px-6 pb-6 animate-in slide-in-from-top-2 duration-300">
                    <div className="bg-slate-50/20 rounded-3xl border border-slate-100">
                        <StreamSection
                            gradeId={grade.id}
                            streams={grade.streams || []}
                            teachers={teachers}
                            onAction={onStreamAction}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

const NewGradeModal = ({ isOpen, onClose, onSuccess, teachers }) => {
    const { post } = useApi();
    const { showToast } = useAppState();
    const [submitting, setSubmitting] = useState(false);
    
    const GRADE_OPTIONS = [
        "Grade 1", "Grade 2", "Grade 3", "Grade 4", "Grade 5", 
        "Grade 6", "Grade 7", "Grade 8", "Grade 9", 
        "Grade 10", "Grade 11", "Grade 12"
    ];
    
    const [gradeData, setGradeData] = useState({
        name: GRADE_OPTIONS[0], // Default to Grade 1
        curriculum_type: 'CBC',
        formative_weight: 40,
        summative_weight: 60,
    });
    
    const [streams, setStreams] = useState([]);

    if (!isOpen) return null;

    const handleAddStream = () => {
        setStreams([...streams, { id: Date.now(), name: '', class_teacher: '', assistant_class_teacher: '' }]);
    };

    const handleRemoveStream = (id) => {
        setStreams(streams.filter(s => s.id !== id));
    };

    const handleStreamChange = (id, field, value) => {
        setStreams(streams.map(s => s.id === id ? { ...s, [field]: value } : s));
    };

    const handleSave = async () => {
        setSubmitting(true);
        try {
            // 1. Create Grade
            const gradeResponse = await post('/api/grade-management/', {
                name: gradeData.name,
                curriculum_type: gradeData.curriculum_type,
                formative_weight: gradeData.formative_weight,
                summative_weight: gradeData.summative_weight,
                order: GRADE_OPTIONS.indexOf(gradeData.name) + 1 // Basic ordering based on index
            });
            
            const gradeId = gradeResponse.id;
            
            // 2. Create Streams
            for (const stream of streams) {
                if (stream.name.trim()) {
                    await post('/api/cbc/streams/', {
                        name: stream.name,
                        grade_level: gradeId,
                        class_teacher: stream.class_teacher || null,
                        assistant_class_teacher: stream.assistant_class_teacher || null
                    });
                }
            }
            
            showToast('Grade and streams created successfully!', 'success');
            onSuccess();
            onClose();
        } catch (err) {
            console.error('Error creating grade:', err);
            showToast(err.response?.data?.error || 'Failed to create grade.', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Create New Grade" size="xl" variant="compact">
            <div className="space-y-6 p-2">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Grade Name</label>
                        <select
                            value={gradeData.name}
                            onChange={(e) => setGradeData({ ...gradeData, name: e.target.value })}
                            className="w-full bg-slate-50 border-none rounded-xl text-sm font-black text-[#18216D] uppercase py-3 focus:ring-2 focus:ring-indigo-100"
                        >
                            {GRADE_OPTIONS.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                        </select>
                    </div>
                </div>
                
                <div className="bg-white p-4 rounded-2xl border border-slate-100">
                    <h4 className="text-[10px] font-black text-[#18216D] uppercase tracking-widest mb-4">Assessment Protocol Weights</h4>
                    <div className="flex items-center gap-6">
                        <div className="flex-1">
                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Formative (%)</label>
                            <input
                                type="number"
                                value={gradeData.formative_weight}
                                onChange={(e) => {
                                    const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                    setGradeData({ ...gradeData, formative_weight: val, summative_weight: 100 - val });
                                }}
                                className="w-full bg-slate-50 border-none rounded-xl text-sm font-black text-[#18216D] py-3 focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Summative (%)</label>
                            <input
                                type="number"
                                value={gradeData.summative_weight}
                                onChange={(e) => {
                                    const val = Math.min(100, Math.max(0, parseFloat(e.target.value) || 0));
                                    setGradeData({ ...gradeData, summative_weight: val, formative_weight: 100 - val });
                                }}
                                className="w-full bg-slate-50 border-none rounded-xl text-sm font-black text-[#18216D] py-3 focus:ring-2 focus:ring-indigo-100"
                            />
                        </div>
                    </div>
                </div>

                <div className="border-t border-slate-100 pt-6">
                    <div className="flex items-center justify-between mb-4">
                        <h4 className="text-[10px] font-black text-[#18216D] uppercase tracking-widest">Enrollment Streams</h4>
                        <button
                            onClick={handleAddStream}
                            className="px-3 py-1.5 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase hover:bg-indigo-100 transition-colors flex items-center gap-2"
                        >
                            <i className="fas fa-plus"></i> Add Stream
                        </button>
                    </div>

                    <div className="space-y-4">
                        {streams.length === 0 ? (
                            <div className="text-center py-6 bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                                <p className="text-[10px] font-bold text-slate-400 uppercase">No streams defined yet. Click "Add Stream" to begin.</p>
                            </div>
                        ) : (
                            streams.map((stream, index) => (
                                <div key={stream.id} className="bg-slate-50 p-4 rounded-2xl border border-slate-200 flex flex-col gap-3 relative group">
                                    <button 
                                        onClick={() => handleRemoveStream(stream.id)}
                                        className="absolute top-3 right-3 text-rose-400 hover:text-rose-600 transition-colors opacity-0 group-hover:opacity-100"
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                    <div>
                                        <label className="text-[9px] font-bold text-slate-400 uppercase tracking-widest block mb-1">Designation / Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. East"
                                            value={stream.name}
                                            onChange={(e) => handleStreamChange(stream.id, 'name', e.target.value)}
                                            className="w-full bg-white border-none rounded-xl text-sm font-black text-[#18216D] uppercase py-2.5 focus:ring-2 focus:ring-indigo-100"
                                        />
                                    </div>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                        <div>
                                            <label className="text-[8px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-1.5 block">Class Teacher</label>
                                            <SearchableDropdown
                                                options={teachers}
                                                selectedValues={stream.class_teacher ? [stream.class_teacher] : []}
                                                onChange={(id) => handleStreamChange(stream.id, 'class_teacher', id)}
                                                placeholder="Select (Optional)"
                                                isMultiple={false}
                                                displayKey="name"
                                                compact={true}
                                            />
                                        </div>
                                        <div className="flex-1">
                                            <label className="text-[8px] font-black text-indigo-400 uppercase tracking-[0.2em] mb-1.5 block">Asst. Teacher</label>
                                            <SearchableDropdown
                                                options={teachers}
                                                selectedValues={stream.assistant_class_teacher ? [stream.assistant_class_teacher] : []}
                                                onChange={(id) => handleStreamChange(stream.id, 'assistant_class_teacher', id)}
                                                placeholder="Select (Optional)"
                                                isMultiple={false}
                                                displayKey="name"
                                                compact={true}
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
            
            <div className="mt-8 flex gap-3">
                <button
                    onClick={handleSave}
                    disabled={submitting}
                    className="flex-1 py-4 bg-indigo-600 text-white rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg shadow-indigo-900/20 hover:bg-indigo-700 transition-all flex items-center justify-center gap-2"
                >
                    {submitting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-check"></i>}
                    {submitting ? 'Creating...' : 'Create Grade & Streams'}
                </button>
                <button
                    onClick={onClose}
                    disabled={submitting}
                    className="px-6 py-4 bg-slate-100 text-slate-500 rounded-2xl text-xs font-black uppercase tracking-widest hover:bg-slate-200 transition-all"
                >
                    Cancel
                </button>
            </div>
        </Modal>
    );
};


const GradeManagement = () => {
    const { get, post, patch, del } = useApi();
    const { showToast } = useAppState();
    const [grades, setGrades] = useState([]);
    const [schoolLevels, setSchoolLevels] = useState([]);
    const [availableTeachers, setAvailableTeachers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [deletingStream, setDeletingStream] = useState(null);
    const [streamDeleting, setStreamDeleting] = useState(false);
    const [hosTeachers, setHosTeachers] = useState([]);
    const [savingHOS, setSavingHOS] = useState(null);
    const [principalTeachers, setPrincipalTeachers] = useState([]);
    const [savingPrincipal, setSavingPrincipal] = useState(null);
    const [isCreatingGrade, setIsCreatingGrade] = useState(false);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [gradesData, teachersData, levelsData, hosData] = await Promise.all([
                get('/api/grade-management/'),
                get('/api/grade-management/available_teachers/'),
                get('/api/cbc/school-levels/'),
                get('/api/teacher/hos/assign/')
            ]);
            setGrades(gradesData);
            setAvailableTeachers(teachersData);
            setSchoolLevels(levelsData);
            setHosTeachers((hosData || []).filter(t => t.is_head_of_studies).map(t => t.id));
            setPrincipalTeachers((hosData || []).filter(t => t.is_principal).map(t => t.id));
        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Failed to query assessment registry.');
        } finally {
            setLoading(false);
        }
    }, [get]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSaveGrade = async (gradeId, data) => {
        try {
            await patch(`/api/grade-management/${gradeId}/update_weights/`, {
                formative_weight: parseFloat(data.formative_weight),
                summative_weight: parseFloat(data.summative_weight)
            });
            await fetchData();
        } catch (err) {
            console.error('Error saving grade configuration:', err);
            showToast(err.response?.data?.error || 'Failed to synchronize grade protocol.', 'error');
        }
    };

    const handleStreamAction = async (type, data) => {
        try {
            if (type === 'add') {
                await post('/api/cbc/streams/', {
                    name: data.name,
                    grade_level: data.grade_level,
                    class_teacher: data.class_teacher || null,
                    assistant_class_teacher: data.assistant_class_teacher || null
                });
            } else if (type === 'update') {
                const payload = {};
                if ('class_teacher' in data) {
                    payload.class_teacher = data.class_teacher || null;
                }
                if ('assistant_class_teacher' in data) {
                    payload.assistant_class_teacher = data.assistant_class_teacher || null;
                }
                await patch(`/api/cbc/streams/${data.id}/`, payload);
            } else if (type === 'delete') {
                setDeletingStream(data);
                return;
            }
            await fetchData();
        } catch (err) {
            console.error('Stream action error:', err);
            showToast('Failed to process stream command.', 'error');
        }
    };

    const handleHOSToggle = async (teacherId, isHOS) => {
        setSavingHOS(teacherId);
        try {
            // Optimistic update
            setHosTeachers(prev => isHOS ? [...prev, teacherId] : prev.filter(id => id !== teacherId));
            await post('/api/teacher/hos/assign/', { teacher_id: teacherId, is_head_of_studies: isHOS });
            showToast(`Head of Studies role ${isHOS ? 'assigned' : 'removed'}.`, 'success');
        } catch (err) {
            console.error('Error assigning HOS:', err);
            setHosTeachers(prev => isHOS ? prev.filter(id => id !== teacherId) : [...prev, teacherId]);
            showToast('Failed to assign leadership protocol.', 'error');
        } finally {
            setSavingHOS(null);
        }
    };

    const handlePrincipalToggle = async (teacherId, isPrincipal) => {
        setSavingPrincipal(teacherId);
        try {
            // Optimistic update
            setPrincipalTeachers(prev => isPrincipal ? [...prev, teacherId] : prev.filter(id => id !== teacherId));
            await post('/api/teacher/hos/assign/', { teacher_id: teacherId, is_principal: isPrincipal });
            showToast(`Principal privileges ${isPrincipal ? 'assigned' : 'removed'}.`, 'success');
        } catch (err) {
            console.error('Error assigning Principal:', err);
            setPrincipalTeachers(prev => isPrincipal ? prev.filter(id => id !== teacherId) : [...prev, teacherId]);
            showToast('Failed to assign leadership protocol.', 'error');
        } finally {
            setSavingPrincipal(null);
        }
    };

    const handleLevelAction = async (data) => {
        try {
            await post('/api/cbc/school-levels/', {
                name: data.name,
                grades: data.grades,
                hod_teachers: data.hod_teachers
            });
            showToast('Department created successfully.', 'success');
            await fetchData();
            return true;
        } catch (err) {
            console.error('Level creation error:', err);
            showToast('Failed to create department.', 'error');
            return false;
        }
    };

    const handleUpdateLevel = async (levelId, data) => {
        try {
            await patch(`/api/cbc/school-levels/${levelId}/`, {
                name: data.name,
                grades: data.grades,
                hod_teachers: data.hod_teachers
            });
            showToast('Department updated successfully.', 'success');
            await fetchData();
            return true;
        } catch (err) {
            console.error('Level update error:', err);
            showToast('Failed to update department.', 'error');
            return false;
        }
    };

    const handleDeleteLevel = async (levelId) => {
        if (!levelId) return;
        try {
            // Optimistic update
            setSchoolLevels(prev => prev.filter(l => l.id !== levelId));
            await del(`/api/cbc/school-levels/${levelId}/`);
            showToast('Department dissolved successfully.', 'success');
            // Background sync
            fetchData();
        } catch (err) {
            console.error('Level deletion error:', err);
            showToast('Failed to dissolve department.', 'error');
            fetchData();
        }
    };

    const confirmDeleteStream = async () => {
        if (!deletingStream) return;
        setStreamDeleting(true);
        try {
            await del(`/api/cbc/streams/${deletingStream}/`);
            await fetchData();
            setDeletingStream(null);
        } catch (err) {
            console.error('Stream deletion error:', err);
            showToast('Failed to terminate stream protocol.', 'error');
        } finally {
            setStreamDeleting(false);
        }
    };

    if (loading) return <LoadingSpinner fullScreen />;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-black text-[#18216D]">Assessment Registry</h1>
                    <p className="text-slate-400 font-bold text-sm mt-1">Define grade levels, structure class streams, and establish grading weight protocols.</p>
                </div>
            </div>

            {error ? (
                <div className="p-12 bg-rose-50 border border-rose-100 rounded-[3rem] text-center">
                    <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-6">
                        <i className="fas fa-exclamation-triangle text-rose-600 text-2xl"></i>
                    </div>
                    <p className="text-rose-600 font-black uppercase text-sm tracking-widest">{error}</p>
                    <button onClick={fetchData} className="mt-6 px-10 py-4 bg-rose-600 text-white rounded-2xl text-[11px] font-black uppercase tracking-widest shadow-lg shadow-rose-900/20">Emergency Sync</button>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-8">
                    <AcademicLeadershipManager 
                        schoolLevels={schoolLevels}
                        teachers={availableTeachers}
                        hosTeachers={hosTeachers}
                        principalTeachers={principalTeachers}
                        grades={grades}
                        onAddLevel={handleLevelAction}
                        onUpdateLevel={handleUpdateLevel}
                        onDeleteLevel={handleDeleteLevel}
                        onToggleHOS={handleHOSToggle}
                        isSavingHOS={savingHOS}
                        onTogglePrincipal={handlePrincipalToggle}
                        isSavingPrincipal={savingPrincipal}
                    />

                    <div className="pt-12 border-t border-slate-100">
                        <div className="flex items-center justify-between mb-8">
                            <h2 className="text-3xl font-black text-[#18216D] tracking-tighter uppercase">Grades & Stream Management</h2>
                            <button
                                onClick={() => setIsCreatingGrade(true)}
                                className="px-6 py-3 bg-[#FFC425] text-[#18216D] rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-[#e5b021] transition-all flex items-center gap-2 shadow-lg shadow-amber-900/20"
                            >
                                <i className="fas fa-plus"></i> New Grade
                            </button>
                        </div>
                        <div className="grid grid-cols-1 gap-8">
                            {grades.map(grade => (
                                <GradeItem
                                    key={grade.id}
                                    grade={grade}
                                    teachers={availableTeachers}
                                    onSave={handleSaveGrade}
                                    onStreamAction={handleStreamAction}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {/* Modals */}
            <NewGradeModal 
                isOpen={isCreatingGrade} 
                onClose={() => setIsCreatingGrade(false)} 
                onSuccess={fetchData} 
                teachers={availableTeachers} 
            />

            <Modal
                isOpen={!!deletingStream}
                onClose={() => setDeletingStream(null)}
                title="Terminate Stream?"
                type="danger"
                footer={
                    <>
                        <button
                            onClick={confirmDeleteStream}
                            disabled={streamDeleting}
                            className="px-8 py-3 bg-rose-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-rose-700 transition-all flex items-center gap-2"
                        >
                            {streamDeleting ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-trash-alt"></i>}
                            {streamDeleting ? 'Terminating...' : 'Confirm Deletion'}
                        </button>
                        <button
                            onClick={() => setDeletingStream(null)}
                            disabled={streamDeleting}
                            className="px-8 py-3 bg-slate-100 text-slate-400 rounded-2xl font-black text-[10px] uppercase tracking-widest hover:bg-slate-200 transition-all"
                        >
                            Cancel
                        </button>
                    </>
                }
            >
                Are you certain you wish to terminate this enrollment stream? All associated teacher assignments will be unlinked. This protocol cannot be reversed.
            </Modal>

        </div>
    );
};

export default GradeManagement;
